import React from 'react';

/**
 * Province / state picker. EMITS THE ISO CODE, shows the name.
 *
 * It used to emit the full name ('Alberta', 'British Columbia') while settings,
 * seeds and imports wrote codes, so production held both spellings in every
 * province column at once — and per-jurisdiction tax has no key until there is
 * one spelling. API migration 0365 canonicalised every column to the code and
 * installed a CHECK that refuses anything else; this component is the other
 * end of that. A stored value in the old spelling still selects the right
 * option, so a form opened on pre-0365 data reads correctly and saves the code.
 */
export const CA_REGIONS: ReadonlyArray<readonly [code: string, name: string]> = [
  ['AB', 'Alberta'], ['BC', 'British Columbia'], ['MB', 'Manitoba'], ['NB', 'New Brunswick'],
  ['NL', 'Newfoundland and Labrador'], ['NS', 'Nova Scotia'], ['NT', 'Northwest Territories'],
  ['NU', 'Nunavut'], ['ON', 'Ontario'], ['PE', 'Prince Edward Island'], ['QC', 'Quebec'],
  ['SK', 'Saskatchewan'], ['YT', 'Yukon'],
];

export const US_REGIONS: ReadonlyArray<readonly [code: string, name: string]> = [
  ['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'], ['CA', 'California'],
  ['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'], ['DC', 'District of Columbia'],
  ['FL', 'Florida'], ['GA', 'Georgia'], ['HI', 'Hawaii'], ['ID', 'Idaho'], ['IL', 'Illinois'],
  ['IN', 'Indiana'], ['IA', 'Iowa'], ['KS', 'Kansas'], ['KY', 'Kentucky'], ['LA', 'Louisiana'],
  ['ME', 'Maine'], ['MD', 'Maryland'], ['MA', 'Massachusetts'], ['MI', 'Michigan'], ['MN', 'Minnesota'],
  ['MS', 'Mississippi'], ['MO', 'Missouri'], ['MT', 'Montana'], ['NE', 'Nebraska'], ['NV', 'Nevada'],
  ['NH', 'New Hampshire'], ['NJ', 'New Jersey'], ['NM', 'New Mexico'], ['NY', 'New York'],
  ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'], ['OK', 'Oklahoma'], ['OR', 'Oregon'],
  ['PA', 'Pennsylvania'], ['RI', 'Rhode Island'], ['SC', 'South Carolina'], ['SD', 'South Dakota'],
  ['TN', 'Tennessee'], ['TX', 'Texas'], ['UT', 'Utah'], ['VT', 'Vermont'], ['VA', 'Virginia'],
  ['WA', 'Washington'], ['WV', 'West Virginia'], ['WI', 'Wisconsin'], ['WY', 'Wyoming'],
];

const ALL: ReadonlyArray<readonly [string, string]> = [...CA_REGIONS, ...US_REGIONS];

/** Code for a stored value in either spelling; '' for blank; the input itself if unknown. */
export function toRegionCode(value: string | null | undefined): string {
  if (!value) return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  const upper = trimmed.toUpperCase();
  if (ALL.some(([code]) => code === upper)) return upper;
  const folded = trimmed.toLowerCase().replace(/\s+/g, ' ');
  const hit = ALL.find(([, name]) => name.toLowerCase() === folded);
  return hit ? hit[0] : trimmed;
}

/** True when `value` is a region code this module knows — the same membership
 *  the API's `isRegionCode` enforces. `toRegionCode` returns its input
 *  unchanged for something it cannot fold, so a caller that must refuse junk
 *  rather than send it checks the result with this. */
export function isRegionCode(value: string | null | undefined): boolean {
  if (!value) return false;
  return ALL.some(([code]) => code === value.trim().toUpperCase());
}

/** Display name for a code; the value itself when it is not one we know. */
export function regionName(value: string | null | undefined): string {
  if (!value) return '';
  const hit = ALL.find(([code]) => code === value.trim().toUpperCase());
  return hit ? hit[1] : value;
}

interface ProvinceSelectProps {
  value: string;
  onChange: (value: string) => void;
  country?: 'CA' | 'US';
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  /** For a <label htmlFor>. Without it the select has no accessible name and a
   *  screen reader — or a test — has to find it by position. */
  id?: string;
  /** An accessible name where the on-screen caption is not a <label> element. */
  'aria-label'?: string;
}

export default function ProvinceSelect({
  value,
  onChange,
  country = 'CA',
  placeholder,
  className,
  disabled,
  id,
  'aria-label': ariaLabel,
}: ProvinceSelectProps) {
  const options = country === 'US' ? US_REGIONS : CA_REGIONS;
  const label = country === 'US' ? 'state' : 'province';
  const selected = toRegionCode(value);
  // A stored code from the OTHER country (a US customer on a CA tenant) must
  // not silently render as "Select province..." and then save as blank.
  const foreign = selected && !options.some(([code]) => code === selected)
    ? ALL.find(([code]) => code === selected)
    : undefined;

  return (
    <select
      id={id}
      aria-label={ariaLabel}
      value={selected}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className={className || 'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm focus:border-fieldiq-accent focus:outline-none'}
    >
      <option value="">{placeholder || `Select ${label}...`}</option>
      {foreign && <option value={foreign[0]}>{foreign[1]}</option>}
      {options.map(([code, name]) => (
        <option key={code} value={code}>{name}</option>
      ))}
    </select>
  );
}
